import React, { useState, useEffect } from 'react';
import { Download, Calendar, FileText, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import EmailGateModal from '../components/EmailGateModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resources = () => {
  const [insights, setInsights] = useState([]);
  const [resources, setResources] = useState([]);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [loadingResources, setLoadingResources] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    fetchInsights();
    fetchResources();
  }, []);

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/insights`);
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error("Failed to load market insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error("Failed to load resources");
    } finally {
      setLoadingResources(false);
    }
  };

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setEmailModalOpen(true);
  };

  const handleInsightClick = (insight) => {
    if (insight.resource_id) {
      const resource = resources.find(r => r.id === insight.resource_id);
      if (resource) {
        handleDownloadClick(resource);
      }
    } else {
      toast.info("Full report coming soon", {
        description: "This insight will be available for download shortly.",
      });
    }
  };

  const handleEmailSubmit = async (email) => {
    try {
      if (!selectedResource) return;
      
      // Create download URL with email parameter
      const downloadUrl = `${API}/resources/${selectedResource.id}/download?email=${encodeURIComponent(email)}`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', selectedResource.file_name);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Download Started", {
        description: `Downloading ${selectedResource.title}...`,
      });
      
      setEmailModalOpen(false);
      setSelectedResource(null);
    } catch (error) {
      console.error('Error downloading resource:', error);
      toast.error("Download Failed", {
        description: "Please try again later.",
      });
    }
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <h1 className="display-huge">Resources & Insights</h1>
        <p className="body-large hero-description">
          Market analysis, technical guides, and industry insights for bauxite trading
        </p>
      </section>

      {/* Market Insights */}
      <section className="insights-section">
        <div className="section-header">
          <h2 className="display-medium">Market Insights</h2>
          <p className="body-medium section-subtitle">
            Stay informed with our latest market analysis and industry trends
          </p>
        </div>
        {loadingInsights ? (
          <div className="loading-message">Loading insights...</div>
        ) : insights.length > 0 ? (
          <div className="insights-grid">
            {insights.map((insight) => (
              <article key={insight.id} className="insight-card">
                <div className="insight-meta">
                  <span className="insight-category">{insight.category}</span>
                  <span className="insight-date">
                    <Calendar size={14} />
                    {new Date(insight.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <h3 className="heading-2">{insight.title}</h3>
                <p className="body-medium">{insight.excerpt}</p>
                <div className="insight-footer">
                  <span className="insight-read-time">{insight.read_time}</span>
                  <button 
                    className="insight-read-more"
                    onClick={() => handleInsightClick(insight)}
                  >
                    {insight.resource_id ? 'Read Report' : 'Read More'}
                    <ArrowRight size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-message">No insights available at the moment.</div>
        )}
      </section>

      {/* Downloadable Resources */}
      <section className="downloads-section">
        <div className="section-header">
          <h2 className="display-medium">Download Resources</h2>
          <p className="body-medium section-subtitle">
            Access our comprehensive guides, brochures, and technical documentation
          </p>
        </div>
        {loadingResources ? (
          <div className="loading-message">Loading resources...</div>
        ) : resources.length > 0 ? (
          <div className="downloads-grid">
            {resources.map((resource) => (
              <div key={resource.id} className="download-card">
                <div className="download-icon">
                  <FileText size={40} />
                </div>
                <div className="download-info">
                  <h3 className="heading-3">{resource.title}</h3>
                  <p className="body-small">{resource.description}</p>
                  <div className="download-meta">
                    <span className="download-type">{resource.file_type}</span>
                    <span className="download-size">{resource.file_size}</span>
                  </div>
                </div>
                <button 
                  className="btn-primary download-btn"
                  onClick={() => handleDownloadClick(resource)}
                >
                  <Download size={20} />
                  Download
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-message">No downloadable resources available yet. Check back soon!</div>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="display-large">Ready to Get Started?</h2>
          <p className="body-large">
            Our team is ready to answer your questions and provide detailed information about our services.
          </p>
          <Link to="/contact" className="btn-primary">
            Contact Us
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Email Gate Modal */}
      <EmailGateModal
        isOpen={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setSelectedResource(null);
        }}
        onSubmit={handleEmailSubmit}
        resourceTitle={selectedResource?.title || ''}
      />
    </div>
  );
};

export default Resources;